import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { INTENT_OPTIONS } from '../lib/pillars';

export const ExperienceIntent = ({ onSelect, onBack, contextLabel }) => {
  const [selected, setSelected] = useState([]);

  const toggleIntent = (value) => {
    setSelected(prev => {
      if (prev.includes(value)) {
        return prev.filter(v => v !== value);
      }
      if (prev.length >= 2) return prev;
      return [...prev, value];
    });
  };

  const canContinue = selected.length >= 1;

  return (
    <section className="min-h-screen py-24 px-6" data-testid="intent-section">
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          <span className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground">
            Step 2
          </span>
          <h2 className="text-2xl sm:text-3xl font-light text-foreground mt-2 mb-3">
            What kind of experience are you looking for in this context?
          </h2>
          {contextLabel && (
            <p className="text-sm text-[hsl(var(--teal))] mb-2">Context: {contextLabel}</p>
          )}
          <p className="text-muted-foreground text-sm">
            The same situation can feel completely different depending on intent.
            Select the type of experience you want to understand. You can choose one or two.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="space-y-3"
        >
          {INTENT_OPTIONS.map((option) => {
            const isSelected = selected.includes(option.value);
            const isDisabled = !isSelected && selected.length >= 2;

            return (
              <Card
                key={option.value}
                onClick={() => !isDisabled && toggleIntent(option.value)}
                className={`p-5 cursor-pointer transition-all border ${
                  isSelected
                    ? 'border-[hsl(var(--teal))] bg-[hsl(var(--teal)/0.05)]'
                    : isDisabled
                      ? 'border-border opacity-40 cursor-not-allowed'
                      : 'border-border hover:border-[hsl(var(--teal)/0.3)]'
                }`}
                data-testid={`intent-option-${option.value}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <span className="text-foreground font-light block mb-1">{option.label}</span>
                    <span className="text-xs text-muted-foreground">{option.description}</span>
                  </div>
                  {isSelected && (
                    <span className="text-xs text-[hsl(var(--teal))] font-medium shrink-0 ml-3">Selected</span>
                  )}
                </div>
              </Card>
            );
          })}
        </motion.div>

        {selected.length > 0 && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-xs text-muted-foreground mt-4"
          >
            {selected.length === 1 ? 'You can select one more if you want.' : '2 selected (maximum).'}
          </motion.p>
        )}

        <motion.div
          className="flex justify-between mt-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <Button
            onClick={onBack}
            variant="ghost"
            className="rounded-full px-6"
            data-testid="intent-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button
            onClick={() => onSelect(selected)}
            disabled={!canContinue}
            className="rounded-full px-8"
            data-testid="intent-continue-btn"
          >
            Continue
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </motion.div>
      </div>
    </section>
  );
};
